import {
  DataTable,
  DataTableColumn,
  DataTableOptions,
  DataType,
  FormDialogMode,
  SelectOption,
  useNotify,
  UserPermission
} from '@globaleris/ui.library'
import { CardContent, makeStyles, useTheme } from '@material-ui/core'
import { Delete, SwapHoriz } from '@material-ui/icons'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { EnumCommodity, EnumTipoCalculoConcepto } from '../../../../api'
import { ConceptRelatedModel } from '../../../../api/Argentia/endpoints/concepts/model/concepts'
import { EnumClientTypes } from '../../../../api/Argentia/enums/enumClientTypes'
import { StandardCard } from '../../../../components/shared/standardCard/standardCard'
import {
  FormDialogDefinitionProps,
  useConfirmationDialog,
  useFormDialog,
  usePageState
} from '../../../../hooks'
import { ConfirmationDialogs } from '../../../../model/enums/confirmationDialogs'
import { getCustomerDomain, storeDataTableRowsPerPageForCurrentPage } from '../../../../redux'
import { isConceptoAgregador } from '../../concepts.helpers'
import {
  deleteLinkRelationalConcept,
  getAggregatorConcepts,
  getNonAggregatorConcepts,
  linkRelationalConcept,
  updateLinkRelationalConcept
} from '../../concepts.service'
import { styles } from './relatedConceptCard.styles'

export interface ConceptIncludedCardProps {
  conceptId: number
  calculationType: EnumTipoCalculoConcepto
  relatedConcepts: ConceptRelatedModel[] | undefined | null
  isFetching: boolean
  canAddRelatedConcepts?: boolean
  clientType: EnumClientTypes
  commodity: EnumCommodity
  ivaTypeOptions?: SelectOption[]
  onRefresh?: () => void
  handleEditRelatedConceptClick?: (relatedConceptId: number) => void
  handleDeleteRelatedConceptClick?: (relatedConceptId: number) => void
}

/**
 * Este componente se encarga de mostrar los conceptos relacionados a
 * un concepto base, permitiendo agregar o eliminar relaciones
 * entre conceptos.
 * Tiene un comportamiento diferente segun el tipo de calculo del concepto base (si es concepto agregador o no),
 * Los conceptos agregadores permiten asociar conceptos con ellos mismos
 * Los conceptos comunes permiten ser asociados a conceptos agregadores
 *
 */
export const RelatedConceptCard: React.FC<ConceptIncludedCardProps> = ({
  conceptId,
  calculationType,
  relatedConcepts,
  isFetching,
  clientType,
  commodity,
  ivaTypeOptions = [],
  canAddRelatedConcepts = false,
  onRefresh,
  handleEditRelatedConceptClick,
  handleDeleteRelatedConceptClick
}) => {
  const [t] = useTranslation(['conceptsEdit', 'dataTable'])
  const { info, error } = useNotify()
  const currentTheme = useTheme()
  const useStyles = makeStyles(styles(currentTheme))
  const classes = useStyles()

  const { openForm, closeForm, formProps, CustomFormDialog, setFormWaiting, updateFormDefinition } =
    useFormDialog()
  const { getConfirmation, closeConfirmation } = useConfirmationDialog()
  const { dataTableRowsPerPage } = usePageState()
  const [availableConcepts, setAvailableConcepts] = useState<SelectOption[]>([])
  const [isLoadingAvailableConcepts, setIsLoadingAvailableConcepts] = useState<boolean>(false)

  // Filtrar conceptos relacionados que no tienen productId
  const conceptsIncluded =
    relatedConcepts?.filter(
      rc => rc.productId == null || rc.productId === 0 || rc.productId === undefined
    ) ?? []
  const noData = !conceptsIncluded || conceptsIncluded.length === 0

  //
  const conceptoAgregadorMode = isConceptoAgregador(calculationType)

  // #region Handlers Starts
  const onAddRelatedConceptClick = async () => {
    // Set loading state and open form first
    setIsLoadingAvailableConcepts(true)
    setAvailableConcepts([])

    // Open form dialog with loading state
    openForm({
      definition: addRelatedConceptFormDefinition,
      title: t('conceptsEdit:components.relatedConceptCard.form.title'),
      mode: FormDialogMode.ADD,
      isWaiting: false,
      onClose: onAddRelatedConceptCloseHandler
    })

    // Load available concepts based on concept type
    // Si es agregador → cargar conceptos NO agregadores para relacionar
    // Si es común → cargar conceptos agregadores para relacionarse con ellos
    const response = conceptoAgregadorMode
      ? await getNonAggregatorConcepts({
          clientType,
          commodity,
          includeInactives: false
        })
      : await getAggregatorConcepts({
          clientType,
          commodity,
          includeInactives: false
        })

    if (response.success && response.data) {
      // Filter out already related concepts
      const relatedIds = conceptsIncluded.map(
        rc =>
          conceptoAgregadorMode
            ? rc.relatedConceptId // En modo agregador, el concepto relacionado es el relatedConceptId
            : rc.baseConceptId // En modo común, el concepto relacionado es el baseConceptId
      )

      // Los datos ya vienen como SelectOption[] desde el backend
      const filtered = response.data.filter(
        option => option.value !== conceptId && !relatedIds.includes(option.value as number)
      )

      setAvailableConcepts(filtered)
      setIsLoadingAvailableConcepts(false)
    } else {
      error(t('conceptsEdit:notifications.fetchAvailableConcepts.error'))
      setIsLoadingAvailableConcepts(false)
      closeForm()
      return false
    }

    return true
  }

  const onAddRelatedConceptCloseHandler = async (formData: { relationalConceptId: number }) => {
    if (formData && formData.relationalConceptId) {
      setFormWaiting(true)

      const response = await linkRelationalConcept({
        conceptId: conceptoAgregadorMode
          ? // Si es concepto agregador, el concepto base es el del componente
            conceptId
          : formData.relationalConceptId,
        relationalConceptId: conceptoAgregadorMode
          ? // Si es concepto agregador, el concepto relacionado es el seleccionado en el form
            formData.relationalConceptId
          : conceptId
      })

      if (response.success) {
        info(t('conceptsEdit:notifications.linkRelationalConcept.success'))
        onRefresh && onRefresh()
      } else {
        error(t('conceptsEdit:notifications.linkRelationalConcept.error'))
      }

      setFormWaiting(false)
    }

    closeForm()
  }

  const onDeleteRelatedConceptHandler = async (row: ConceptRelatedModel) => {
    await getConfirmation(ConfirmationDialogs.DELETE_CONCEPT_RELATED).then(async () => {
      const response = await deleteLinkRelationalConcept({
        conceptId: row.baseConceptId,
        relationalConceptId: row.relatedConceptId
      })

      if (response.success) {
        info(t('conceptsEdit:notifications.deleteLinkRelationalConcept.success'))
        onRefresh && onRefresh()
      } else {
        error(t('conceptsEdit:notifications.deleteLinkRelationalConcept.error'))
      }

      closeConfirmation()
    })
  }

  const onChangeIVATypeHandler = (row: ConceptRelatedModel) => {
    openForm({
      definition: changeIVATypeFormDefinition(row),
      title: t('conceptsEdit:components.relatedConceptCard.changeIVATypeForm.title'),
      mode: FormDialogMode.EDIT,
      isWaiting: false,
      onClose: (formData: { ivaTypeConceptId: number }) =>
        onChangeIVATypeCloseHandler(formData, row)
    })
  }

  const onChangeIVATypeCloseHandler = async (
    formData: { ivaTypeConceptId: number },
    row: ConceptRelatedModel
  ) => {
    if (formData?.ivaTypeConceptId) {
      setFormWaiting(true)

      const response = await updateLinkRelationalConcept({
        conceptId: row.baseConceptId,
        relationalConceptId: row.relatedConceptId,
        ivaTypeConceptId: formData.ivaTypeConceptId
      })

      if (response.success) {
        info(t('conceptsEdit:notifications.updateLinkRelationalConcept.success'))
        onRefresh && onRefresh()
      } else {
        error(t('conceptsEdit:notifications.updateLinkRelationalConcept.error'))
      }

      setFormWaiting(false)
    }

    closeForm()
  }

  // #endregion Handlers Ends

  // #region DataTable config Starts
  const columns: DataTableColumn[] = [
    {
      name: 'relatedConceptCode',
      label: t('conceptsEdit:components.relatedConceptCard.dataTable.columns.code'),
      type: DataType.TEXT,
      columnWidth: 200
    },
    {
      name: 'relatedConceptDesc',
      label: t('conceptsEdit:components.relatedConceptCard.dataTable.columns.description'),
      type: DataType.TEXT
    }
  ]

  const dataTableOptions: DataTableOptions = {
    pagination: true,
    rowsPerPage: dataTableRowsPerPage,
    onRowsPerPageChange: selectedValue => storeDataTableRowsPerPageForCurrentPage(selectedValue),
    search: false,
    download: false,
    canEdit: false,
    canDelete: false,
    canInsert: canAddRelatedConcepts,
    customInsertEditModal: true,
    onRowInsert: onAddRelatedConceptClick,
    elevation: 0,
    analytics: {
      customer: getCustomerDomain(),
      view: t('conceptsEdit:components.relatedConceptCard.dataTable.analytics')
    },
    rowCustomActions: canAddRelatedConcepts
      ? [
          {
            icon: <SwapHoriz fontSize="small" />,
            tooltip: t(
              'conceptsEdit:components.relatedConceptCard.dataTable.actions.changeIVAType'
            ),
            onClick: onChangeIVATypeHandler,
            userPermission: UserPermission.EDIT,
            visibleIf: 'canChangeIVAType'
          },
          {
            icon: <Delete fontSize="small" />,
            tooltip: t('conceptsEdit:components.relatedConceptCard.dataTable.actions.delete'),
            onClick: onDeleteRelatedConceptHandler,
            userPermission: UserPermission.EDIT
          }
        ]
      : []
  }

  // #endregion DataTable config Ends

  // #region Form Definition Starts

  const changeIVATypeFormDefinition = (row: ConceptRelatedModel): FormDialogDefinitionProps => ({
    id: 'CHANGE_IVA_TYPE',
    sections: [
      {
        inputs: [
          {
            name: 'ivaTypeConceptId',
            label: t('conceptsEdit:components.relatedConceptCard.changeIVATypeForm.inputs.ivaType'),
            type: DataType.SELECT,
            selectOptions: ivaTypeOptions,
            required: true
          }
        ]
      }
    ]
  })

  const addRelatedConceptFormDefinition =
    React.useMemo<FormDialogDefinitionProps>((): FormDialogDefinitionProps => {
      return {
        id: 'ADD_RELATED_CONCEPT',
        sections: [
          {
            inputs: [
              {
                name: 'relationalConceptId',
                label: t('conceptsEdit:components.relatedConceptCard.form.inputs.concept'),
                type: DataType.SELECT,
                selectOptions: availableConcepts,
                isLoading: isLoadingAvailableConcepts,
                required: true
              }
            ]
          }
        ]
      }
    }, [isLoadingAvailableConcepts, availableConcepts, t])

  useEffect(() => {
    updateFormDefinition(addRelatedConceptFormDefinition)
  }, [addRelatedConceptFormDefinition])

  // #endregion Form Definition Ends

  return (
    <>
      <StandardCard
        title={
          conceptoAgregadorMode
            ? t('conceptsEdit:components.relatedConceptCard.agregadorTitle')
            : t('conceptsEdit:components.relatedConceptCard.title')
        }
        isLoading={isFetching}
        showEmptyContent={noData}
        raised
        className={classes.card}
        emptyCaption={t('conceptsEdit:components.relatedConceptCard.emptyCard.description')}
        emptyButtonCallback={onAddRelatedConceptClick}
        emptyButtonCaption={t('conceptsEdit:components.relatedConceptCard.emptyCard.buttonCaption')}
      >
        <CardContent>
          <DataTable
            columns={columns}
            data={conceptsIncluded}
            options={dataTableOptions}
            userPermissions={
              canAddRelatedConcepts ? [UserPermission.EDIT] : [UserPermission.READ_ONLY]
            }
          />
        </CardContent>
      </StandardCard>
      <CustomFormDialog {...formProps} />
    </>
  )
}
